// Hosts the EAITF and EAITT inventory-transfer workflow, including lookup and receipt generation.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import html2pdf from 'html2pdf.js';
import moment from 'moment';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@rentacenter/racstrap';

import {
  GetInventoryInformation,
  InventoryInformationResponse,
} from '../../../api/user';
import { printHTML } from '../../shared/print';
import { InventoryTransferDetails, StoreAction } from './storeActionDomain';
import { getStoreActionMessage } from './storeActionLocalization';
import { useStoreActionTransferTransaction } from './storeActionMutations';

type StoreActionTransferDialogProps = {
  readonly activation: StoreActionTransferActivation | null;
  readonly onClose: () => void;
  readonly storeNumber: string;
};

export type StoreActionTransferActivation = {
  readonly item: StoreAction;
  readonly trigger: HTMLElement;
  readonly type: StoreActionTransferType;
};

export type StoreActionTransferType = 'EAITF' | 'EAITT';

const INVALID_INVENTORY_TRANSFER_RESPONSE =
  'Invalid inventory transfer response';

export const hasInventoryId = (item: StoreAction): boolean =>
  typeof item.inventoryId === 'string'
    ? Boolean(item.inventoryId.trim())
    : typeof item.inventoryId === 'number';

const selectInventoryTransferDetails = (
  response: InventoryInformationResponse
): InventoryTransferDetails => {
  if (response.status !== 200) {
    throw new Error(INVALID_INVENTORY_TRANSFER_RESPONSE);
  }

  return response.data as InventoryTransferDetails;
};

const TRANSFER_PDF_ERROR = 'Transfer PDF conversion failed';

const displayValue = (value: string | number | undefined): string =>
  value === undefined || value === '' ? '-' : String(value);

const EMPLOYEE_ID = 'Employee Id';
const SIGNATURE_LINE_BORDER = '2px solid #000';
const SIGNATURE_LABELS = ['Printed Name', 'Signature'];

const endpoint = (
  storeNumber: string | number | undefined,
  city: string | undefined,
  state: string | undefined
): string =>
  [displayValue(storeNumber), city, state].filter(Boolean).join(', ');

const printStoreLocation = (
  storeNumber: string | number | undefined,
  city: string | undefined,
  state: string | undefined
): string => `${displayValue(storeNumber)} (${city || ''},${state || ''})`;

const getPrintStores = (
  isOut: boolean,
  storeNumber: string,
  transfer: InventoryTransferDetails['inventoryTransferInformation']
): { receiver: string; sender: string } => {
  if (isOut) {
    return {
      receiver: displayValue(transfer?.receivingStoreNumber),
      sender: displayValue(storeNumber),
    };
  }

  return {
    receiver: printStoreLocation(
      storeNumber,
      transfer?.receivingStoreCity,
      transfer?.receivingStoreState
    ),
    sender: printStoreLocation(
      transfer?.transferringStoreNumber,
      transfer?.transferringStoreCity,
      transfer?.transferringStoreState
    ),
  };
};

const isCustomerAgreement = (details: InventoryTransferDetails): boolean =>
  Boolean(details.currentAgreement?.customerName?.trim());

const getAcknowledgementKey = (
  type: StoreActionTransferType,
  details: InventoryTransferDetails
): Parameters<typeof getStoreActionMessage>[0] => {
  const customerAgreement = isCustomerAgreement(details);
  if (type === 'EAITF') {
    return customerAgreement ? 'transferOutCustomer' : 'transferOutSalesLead';
  }

  return customerAgreement ? 'transferInCustomer' : 'transferInSalesLead';
};

const getAcknowledgement = (
  type: StoreActionTransferType,
  details: InventoryTransferDetails
): string => {
  const transfer = details.inventoryTransferInformation;
  return getStoreActionMessage(getAcknowledgementKey(type, details))
    .replace(
      '{{customerName}}',
      details.currentAgreement?.customerName?.trim() || ''
    )
    .replace(
      '{{itemNumber}}',
      displayValue(details.inventoryHeader?.itemNumber)
    )
    .replace(
      type === 'EAITF'
        ? '{{receivingStoreNumber}}'
        : '{{transferringStoreNumber}}',
      displayValue(
        type === 'EAITF'
          ? transfer?.receivingStoreNumber
          : transfer?.transferringStoreNumber
      )
    );
};

const pdfToBase64 = (pdf: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(TRANSFER_PDF_ERROR));
    reader.onloadend = () => {
      const value = reader.result;
      if (typeof value !== 'string' || !value.includes(',')) {
        reject(new Error(TRANSFER_PDF_ERROR));
        return;
      }
      resolve(value.split(',', 2)[1]);
    };
    reader.readAsDataURL(pdf);
  });

export const StoreActionTransferDialog: React.FC<StoreActionTransferDialogProps> =
  ({ activation, onClose, storeNumber }) => {
    const printableFormRef = React.useRef<HTMLDivElement | null>(null);
    const pdfRef = React.useRef<string | null>(null);
    const isPrintingRef = React.useRef(false);
    const [printError, setPrintError] = React.useState<Error | null>(null);
    const item = activation?.item;
    const isEnabled = Boolean(item && hasInventoryId(item));
    const query = useQuery({
      enabled: isEnabled,
      queryKey: ['store-action', 'inventory-transfer', item?.inventoryId],
      queryFn: () => {
        const inventoryId = item?.inventoryId;

        if (
          typeof inventoryId !== 'string' &&
          typeof inventoryId !== 'number'
        ) {
          throw new Error('Missing inventory identifier');
        }

        return GetInventoryInformation(inventoryId);
      },
      select: selectInventoryTransferDetails,
      useErrorBoundary: true,
    });
    const { startTransferTransaction } =
      useStoreActionTransferTransaction(storeNumber);

    const close = (): void => onClose();

    const details = query.data;
    const transfer = details?.inventoryTransferInformation;
    const isOut = activation?.type === 'EAITF';
    const sender = isOut
      ? endpoint(storeNumber, undefined, undefined)
      : endpoint(
          transfer?.transferringStoreNumber,
          transfer?.transferringStoreCity,
          transfer?.transferringStoreState
        );
    const receiver = isOut
      ? endpoint(
          transfer?.receivingStoreNumber,
          transfer?.receivingStoreCity,
          transfer?.receivingStoreState
        )
      : endpoint(storeNumber, undefined, undefined);
    const { receiver: printReceiver, sender: printSender } = getPrintStores(
      isOut,
      storeNumber,
      transfer
    );

    const print = async (): Promise<void> => {
      if (
        !activation ||
        !details ||
        !printableFormRef.current ||
        isPrintingRef.current
      ) {
        return;
      }

      isPrintingRef.current = true;
      try {
        printHTML(printableFormRef.current);
        const pdf = await html2pdf()
          .from(printableFormRef.current)
          .outputPdf('blob');
        pdfRef.current = await pdfToBase64(pdf);
        const started = await startTransferTransaction({
          item: activation.item,
          onEnd: () => {
            pdfRef.current = null;
            isPrintingRef.current = false;
            close();
          },
          pdf: pdfRef.current,
          type: activation.type,
        });
        if (!started) {
          pdfRef.current = null;
          isPrintingRef.current = false;
        }
      } catch (error: unknown) {
        pdfRef.current = null;
        isPrintingRef.current = false;
        setPrintError(
          error instanceof Error ? error : new Error(TRANSFER_PDF_ERROR)
        );
      }
    };

    if (printError) {
      throw printError;
    }

    return (
      <Dialog
        aria-labelledby="store-action-transfer-title"
        onClose={close}
        open={Boolean(activation)}
      >
        <DialogTitle id="store-action-transfer-title">
          {getStoreActionMessage(
            isOut ? 'transferOutTitle' : 'transferInTitle'
          )}
        </DialogTitle>
        <DialogContent>
          {query.isLoading ? (
            <div aria-busy="true" role="status">
              {getStoreActionMessage('transferLoading')}
            </div>
          ) : null}
          {details && activation ? (
            <>
              <Typography component="p" variant="body1">
                {getAcknowledgement(activation.type, details)}
              </Typography>
              <dl>
                <dt>{getStoreActionMessage('transferItem')}</dt>
                <dd>{displayValue(details.inventoryHeader?.itemNumber)}</dd>
                <dt>{getStoreActionMessage('transferModel')}</dt>
                <dd>
                  {displayValue(details.inventoryInformation?.modelNumber)}
                </dd>
                <dt>{getStoreActionMessage('transferVendor')}</dt>
                <dd>{displayValue(details.inventoryInformation?.vendor)}</dd>
                <dt>{getStoreActionMessage('transferBrand')}</dt>
                <dd>{displayValue(details.inventoryInformation?.brand)}</dd>
                <dt>{getStoreActionMessage('transferSerial')}</dt>
                <dd>
                  {displayValue(details.inventoryInformation?.serialNumber)}
                </dd>
                <dt>{getStoreActionMessage('transferSender')}</dt>
                <dd>{sender}</dd>
                <dt>{getStoreActionMessage('transferReceiver')}</dt>
                <dd>{receiver}</dd>
              </dl>
            </>
          ) : null}
        </DialogContent>
        {details ? (
          <DialogActions>
            <Button color="primary" onClick={print} type="button">
              {getStoreActionMessage('print')}
            </Button>
            <Button color="primary" onClick={close} type="button">
              {getStoreActionMessage('ok')}
            </Button>
          </DialogActions>
        ) : null}
        {details && activation ? (
          <div
            aria-hidden="true"
            ref={printableFormRef}
            style={{
              left: '-10000px',
              position: 'fixed',
              top: 0,
              width: '800px',
            }}
          >
            <div style={{ float: 'left', padding: '3%', width: '94%' }}>
              <div
                style={{ float: 'left', marginBottom: '10px', width: '100%' }}
              >
                <h2 style={{ float: 'left', margin: '0px' }}>
                  {getStoreActionMessage(
                    isOut ? 'transferOutForm' : 'transferInForm'
                  )}
                </h2>
                <div style={{ float: 'right' }}>
                  {moment().format('ddd,D MMM YYYY, hh:mm A')}
                </div>
              </div>
              <div
                style={{ float: 'left', marginBottom: '20px', width: '100%' }}
              >
                <label style={{ float: 'right' }}>
                  {getStoreActionMessage('page')}: 1
                </label>
              </div>
              <div
                style={{ float: 'left', marginBottom: '20px', width: '50%' }}
              >
                <label style={{ margin: '0px' }}>
                  Transferring Store: {printSender}
                </label>
              </div>
              <div
                style={{ float: 'left', marginBottom: '20px', width: '50%' }}
              >
                <label style={{ margin: '0px' }}>
                  {getStoreActionMessage('transferReceiver')}: {printReceiver}
                </label>
              </div>
              <div
                style={{ float: 'left', marginBottom: '20px', width: '100%' }}
              >
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      {[
                        'transferItem',
                        'transferModel',
                        'transferVendor',
                        'transferBrand',
                        'transferSerial',
                      ].map((key) => (
                        <th
                          key={key}
                          style={{
                            borderBottom: '1px solid #000',
                            color: '#000',
                            fontSize: '14px',
                            padding: '7px',
                            textAlign: 'left',
                          }}
                        >
                          {getStoreActionMessage(
                            key as Parameters<typeof getStoreActionMessage>[0]
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {[
                        details.inventoryHeader?.itemNumber,
                        details.inventoryInformation?.modelNumber,
                        details.inventoryInformation?.vendor,
                        details.inventoryInformation?.brand,
                        details.inventoryInformation?.serialNumber,
                      ].map((value, index) => (
                        <td
                          key={index}
                          style={{ fontSize: '14px', paddingTop: '7px' }}
                        >
                          {displayValue(value)}
                        </td>
                      ))}
                    </tr>
                    <tr />
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  float: 'left',
                  marginBottom: '70px',
                  marginTop: '70px',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <div style={{ fontSize: '25px', width: '100%' }}>
                  {getStoreActionMessage('transferSoldNotice')}
                </div>
                <div style={{ fontSize: '25px', width: '100%' }}>
                  Please ask to see another
                </div>
                <div style={{ fontSize: '25px', width: '100%' }}>
                  One just like me!
                </div>
              </div>
              <div style={{ float: 'left', width: '100%' }}>
                <div style={{ float: 'left', width: '50%' }}>
                  <h4>{getStoreActionMessage('deliveredBy')}</h4>
                  {[EMPLOYEE_ID, ...SIGNATURE_LABELS].map((label) => (
                    <div
                      key={label}
                      style={{
                        float: 'left',
                        marginBottom: '30px',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          borderBottom: SIGNATURE_LINE_BORDER,
                          float: 'left',
                          marginBottom: '10px',
                          width: '85%',
                        }}
                      />
                      <div style={{ float: 'left', width: '100%' }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ float: 'left', width: '50%' }}>
                  <h4>{getStoreActionMessage('receivedBy')}</h4>
                  <div
                    style={{
                      float: 'left',
                      marginBottom: '30px',
                      width: '70%',
                    }}
                  >
                    <span
                      style={{
                        borderBottom: SIGNATURE_LINE_BORDER,
                        float: 'left',
                        marginBottom: '10px',
                        width: '85%',
                      }}
                    />
                    <div style={{ float: 'left', width: '100%' }}>
                      {EMPLOYEE_ID}
                    </div>
                  </div>
                  <div
                    style={{
                      float: 'left',
                      marginBottom: '30px',
                      width: '30%',
                    }}
                  >
                    <span
                      style={{
                        borderBottom: SIGNATURE_LINE_BORDER,
                        float: 'left',
                        marginBottom: '10px',
                        width: '70%',
                      }}
                    />
                    <div style={{ float: 'left', width: '100%' }}>Date</div>
                  </div>
                  {SIGNATURE_LABELS.map((label) => (
                    <div
                      key={label}
                      style={{
                        float: 'left',
                        marginBottom: '30px',
                        width: '100%',
                      }}
                    >
                      <span
                        style={{
                          borderBottom: SIGNATURE_LINE_BORDER,
                          float: 'left',
                          marginBottom: '10px',
                          width: '91%',
                        }}
                      />
                      <div style={{ float: 'left', width: '100%' }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>
    );
  };
